import { useState } from 'react';
import { P2GAlert, P2GBox, P2GButton, P2GLink, P2GLinkGroup, P2GText } from '@parcel2go/core-ui';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import {
  OrderLineSyncPlan,
  OrderLineSyncResult,
  SYNCABLE_CONTENT_FIELDS,
  SyncableContentField
} from '~/apps/smartsend/lib/parcels/syncOrderLines';
import { ToInlineCommaList } from '~/apps/smartsend/lib/helpers/StringHelper';

interface props {
  parcelCount: number;
  /** What of the user's line edits can be carried onto the parcels. Absent or empty means idle. */
  plan?: OrderLineSyncPlan;
  onApply?: () => OrderLineSyncResult;
  onRebuild?: () => void;
}

/**
 * Says that the parcels rather than the eCommerce lines are what gets declared, and offers the two
 * routes from an edited line to an updated declaration.
 *
 * The lines stay editable by choice. They are still the imported marketplace record and they still
 * carry things a consignment does not supersede - the 25000 value cap and the 50-character Sku check in
 * SmartSendValidation both walk MarketplaceOrderLines whatever the consignment looks like, so a
 * read-only section could leave a purchase-blocking error with no field able to clear it. What the
 * parcels do supersede is the declared contents: with stored parcels, validation and
 * OrderMapper.MapParcels read Description, TariffCode and CountryOfManufacture from Parcels[].Contents
 * and never look at the lines. Note that this is not a customs-only concern - the declaration goes to
 * the courier on every shipment, and it is what a claim would be assessed against, so the wording below
 * must not imply it only matters for international.
 *
 * APPLY is the scalpel - planOrderLineSync has already established which rows still hold the pre-edit
 * value, so it writes only to rows the user never customised and every other parcel edit survives. It is
 * not confirmed: it writes values the user has just typed, onto rows they never touched.
 *
 * REBUILD is the hammer, and the answer when every matching row was customised so Apply has nowhere
 * safe to write. It regenerates from the lines and loses everything on the parcels, so it IS confirmed.
 *
 * Notably absent: collapsing to a single parcel. That was what "Start over" did here, which is a
 * decision about the shipment rather than about these product details - and the parcel count picker in
 * Shipment Details above already offers it. Both actions here only stage the change; the user still
 * presses Save, and the panel's own unsaved-changes alert is what says so.
 *
 * Deliberately quiet when idle: one line, and info rather than warning. Everything else appears only
 * when there is something to act on, so the orange actually means something when it shows up. There is
 * no standing explanation of which fields can be copied - the Apply button explains itself the moment
 * it appears next to a field the user has just changed.
 *
 * Its own file rather than a local component in ItemContentsPanel so it can be tested directly - the
 * panel reaches ConsignmentDetails and from there the whole grouping tree.
 */
export const ConsignmentContentsNotice: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [confirmingRebuild, setConfirmingRebuild] = useState(false);
  // What actually landed, so the confirmation can report it rather than restate the plan. Never needs
  // clearing: a pending change outranks it, so the next edit swaps it back to the CTA on its own.
  const [applied, setApplied] = useState<OrderLineSyncResult>(null);

  const fieldLabels: Record<SyncableContentField, string> = {
    Description: t('CONTENT_FIELD_DESCRIPTION_LABEL', { defaultValue: 'description' }),
    Sku: t('CONTENT_FIELD_SKU_LABEL', { defaultValue: 'SKU' }),
    TariffCode: t('CONTENT_FIELD_TARIFF_CODE_LABEL', { defaultValue: 'tariff code' }),
    CountryOfManufacture: t('CONTENT_FIELD_COUNTRY_LABEL', { defaultValue: 'country of manufacture' })
  };

  const pending = props.plan?.pending?.length > 0;
  const blocked = !pending && props.plan?.blocked?.length > 0;

  const blockedFields = ToInlineCommaList(
    SYNCABLE_CONTENT_FIELDS.filter((field) => props.plan?.blocked?.some((c) => c.field === field)).map(
      (field) => fieldLabels[field]
    )
  );

  const apply = () => setApplied(props.onApply?.() ?? null);

  /**
   * Only offered when the lines and the parcels actually disagree.
   *
   * With nothing to reconcile there is nothing for a rebuild to achieve - it would regenerate the same
   * declaration it already has, so the link would be a destructive confirm dialog leading to no visible
   * change. It belongs to the pending and blocked states, as the wholesale alternative to Apply and as
   * the only way through when Apply has nowhere safe to write.
   */
  const rebuildLink = (
    <P2GBox mt={1}>
      <P2GLink onClick={() => setConfirmingRebuild(true)}>
        {t('CONSIGNMENT_REBUILD_LINK', {
          defaultValue: 'Rebuild my {{parcelCount}} parcels from these details',
          parcelCount: props.parcelCount
        })}
      </P2GLink>
    </P2GBox>
  );

  if (confirmingRebuild) {
    return (
      <P2GAlert type="warning">
        <P2GText component="div" variant="body2">
          {t('CONSIGNMENT_REBUILD_CONFIRM_TEXT', {
            defaultValue:
              'This will rebuild {{parcelCount}} parcels from the details below. Everything you have changed on those parcels will be lost.',
            parcelCount: props.parcelCount
          })}
          <P2GLinkGroup
            pt={1}
            links={[
              <P2GLink
                onClick={() => {
                  setApplied(null);
                  setConfirmingRebuild(false);
                  props.onRebuild?.();
                }}
                key="confirm"
              >
                {t('CONSIGNMENT_REBUILD_CONFIRM_LINK', { defaultValue: 'Yes, rebuild my parcels' })}
              </P2GLink>,
              <P2GLink onClick={() => setConfirmingRebuild(false)} key="cancel">
                {t('CONSIGNMENT_CLEAR_CANCEL_LINK', { defaultValue: 'Cancel' })}
              </P2GLink>
            ]}
          />
        </P2GText>
      </P2GAlert>
    );
  }

  const headline = (
    <strong>
      {/* Deliberately not "declared to customs" - that is only true of international shipments, and
          this notice shows for domestic ones too. What holds either way is that the parcel contents are
          what the courier is given and what a claim would be assessed against. */}
      {t('CONSIGNMENT_CONTENTS_NOTICE', {
        defaultValue:
          'You are using a {{parcelCount}} parcel custom shipment. Your parcel contents are what we declare to the courier and what any claim is based on, not the items below.',
        parcelCount: props.parcelCount
      })}
    </strong>
  );

  if (pending) {
    return (
      <P2GAlert type="warning">
        <P2GText component="div" variant="body2">
          {headline}
          <P2GBox mt={1}>
            {/* Two keys rather than i18next's plural suffixes: nothing else in this codebase relies on
                plural resolution, so the i18n config for it is untested. */}
            {props.plan.pending.length === 1
              ? t('CONSIGNMENT_SYNC_PENDING_ONE_TEXT', {
                  defaultValue: '1 change not applied to your parcels.'
                })
              : t('CONSIGNMENT_SYNC_PENDING_TEXT', {
                  defaultValue: '{{count}} changes not applied to your parcels.',
                  count: props.plan.pending.length
                })}
          </P2GBox>
          <P2GBox mt={2}>
            <P2GButton variant="primary" onClick={apply}>
              {t('CONSIGNMENT_SYNC_APPLY_BUTTON', { defaultValue: 'Apply to my parcels' })}
            </P2GButton>
          </P2GBox>
          {rebuildLink}
        </P2GText>
      </P2GAlert>
    );
  }

  if (applied?.applied) {
    return (
      <P2GAlert type="success">
        <P2GText component="div" variant="body2">
          {headline}
          <P2GBox mt={1}>
            {/* The "x of y" form is only reachable when something was skipped, so y is always at least
                two and "items" is never wrong. Saying "1 of 1" when nothing was held back would be
                both odd and pointless. */}
            {applied.skippedRows > 0
              ? t('CONSIGNMENT_SYNC_APPLIED_PARTIAL_TEXT', {
                  defaultValue: 'Applied to {{updated}} of {{total}} parcel items.',
                  updated: applied.updatedRows,
                  total: applied.updatedRows + applied.skippedRows
                })
              : t('CONSIGNMENT_SYNC_APPLIED_TEXT', { defaultValue: 'Applied to your parcels.' })}
          </P2GBox>
        </P2GText>
      </P2GAlert>
    );
  }

  if (blocked) {
    return (
      <P2GAlert type="warning">
        <P2GText component="div" variant="body2">
          {headline}
          <P2GBox mt={1}>
            {t('CONSIGNMENT_SYNC_BLOCKED_TEXT', {
              defaultValue: '{{fields}} not applied - those parcel items have their own values.',
              fields: blockedFields
            })}
          </P2GBox>
          {rebuildLink}
        </P2GText>
      </P2GAlert>
    );
  }

  return (
    <P2GAlert type="info">
      <P2GText component="div" variant="body2">
        {headline}
      </P2GText>
    </P2GAlert>
  );
};
